import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/app-shell";
import { requireRole, useSession } from "@/lib/session";
import { useParentContext, type ParentChild } from "@/lib/parent-context";
import { supabase } from "@/integrations/supabase/client";
import { generateChildSummary } from "@/lib/parent-ai.functions";
import { cn } from "@/lib/utils";
import { WeeklySummaryCard } from "@/components/intelligence/weekly-summary-card";
import {
  Sparkles, CalendarCheck, Wallet, Utensils, ClipboardList,
  ShieldCheck, HeartPulse, Bus, Megaphone, MessageSquare, CalendarDays,
  ArrowRight, AlertTriangle, Loader2, ChevronRight, Clock, MapPin,
  Link2,
} from "lucide-react";
import { AICallout } from "@/components/makai/primitives";

export const Route = createFileRoute("/parent/")({
  ssr: false,
  beforeLoad: () => requireRole("parent"),
  head: () => ({ meta: [{ title: "Family — MAKAI" }] }),
  component: ParentHome,
});

interface ChildSnapshot {
  attendancePct: number;
  attendanceDays: number;
  absentCount: number;
  pendingHomework: number;
  overdueHomework: number;
  feeBalance: number;
  feeOverdue: number;
  cafeteriaBalance: number | null;
  cafeteriaLow: boolean;
  recentScores: { subject: string; title: string; pct: number; date: string }[];
  incidents: { id: string; category: string; severity: string; date: string; description: string }[];
  medicalAlerts: { id: string; kind: string; title: string; date: string }[];
  todayTimetable: { id: string; start: string; end: string; subject: string; room: string | null }[];
  homeworkDue: { id: string; title: string; subject: string; due: string | null }[];
  upcomingExams: { id: string; title: string; subject: string; date: string }[];
  announcements: { id: string; title: string; body: string; created_at: string; pinned: boolean }[];
  messages: { id: string; subject: string | null; body: string; created_at: string; read_at: string | null; sender: string }[];
  events: { id: string; title: string; start_at: string; location: string | null; type: string }[];
  transport: {
    routeName: string;
    vehicleReg: string | null;
    driverName: string | null;
    driverPhone: string | null;
    pickupPoint: string | null;
    lastPing: { status: string; recorded_at: string; lat: number | null; lng: number | null } | null;
  } | null;
}

const DAYS = ["sun","mon","tue","wed","thu","fri","sat"] as const;

function todayKey(): typeof DAYS[number] { return DAYS[new Date().getDay()]; }

function fmtMoney(n: number, currency: string) {
  return `${currency} ${Math.round(n).toLocaleString()}`;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function ParentHome() {
  const { session } = useSession();
  const { ctx, loading, error } = useParentContext();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Record<string, ChildSnapshot>>({});
  const [snapLoading, setSnapLoading] = useState(false);

  useEffect(() => {
    if (!ctx || ctx.children.length === 0) return;
    if (!activeId) setActiveId(ctx.children[0].learnerId);
  }, [ctx, activeId]);

  useEffect(() => {
    if (!ctx) return;
    let cancelled = false;
    (async () => {
      setSnapLoading(true);
      const next: Record<string, ChildSnapshot> = {};
      for (const child of ctx.children) {
        next[child.learnerId] = await fetchChildSnapshot(child);
        if (cancelled) return;
      }
      if (!cancelled) { setSnapshots(next); setSnapLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [ctx]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  const firstName = (session?.name ?? "Parent").split(" ")[0];

  if (loading) {
    return (
      <AppShell role="parent" title="Family">
        <div className="grid place-items-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell role="parent" title="Family">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">{error}</div>
      </AppShell>
    );
  }

  if (!ctx || ctx.children.length === 0) {
    return (
      <AppShell role="parent" title="Family">
        <div className="rounded-3xl border border-border bg-card p-8 text-center shadow-soft">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gradient-gold text-gold-foreground"><Link2 className="h-6 w-6" /></div>
          <h2 className="mt-4 font-display text-xl font-bold">Link a child to begin</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Use the 8-character code from your child's profile to securely connect their school account.</p>
          <Link to="/parent/link" className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-soft">
            <Link2 className="h-4 w-4" /> Link a child
          </Link>
        </div>
      </AppShell>
    );
  }

  const active = ctx.children.find((c) => c.learnerId === activeId) ?? ctx.children[0];
  const snap = snapshots[active.learnerId];

  return (
    <AppShell role="parent" title={`${greeting}, ${firstName}`} subtitle="Your family's school life — all in one place.">
      {/* Children switcher */}
      <div className="-mx-1 mb-5 flex gap-2 overflow-x-auto px-1 pb-1">
        {ctx.children.map((c) => {
          const s = snapshots[c.learnerId];
          const isActive = c.learnerId === active.learnerId;
          return (
            <button
              key={c.learnerId}
              type="button"
              onClick={() => setActiveId(c.learnerId)}
              className={cn(
                "group flex shrink-0 items-center gap-3 rounded-2xl border px-3 py-2.5 text-left transition-all",
                isActive ? "border-primary bg-primary-soft shadow-soft" : "border-border bg-card hover:border-primary/40",
              )}
            >
              <Avatar child={c} size={40} />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold leading-tight">{c.firstName} {c.lastName}</div>
                <div className="truncate text-[11px] text-muted-foreground">{c.gradeLabel ?? c.schoolName ?? "Linked"}</div>
              </div>
              {s?.feeOverdue ? <span className="ml-1 h-2 w-2 rounded-full bg-destructive" aria-label="Has overdue fees" /> : null}
            </button>
          );
        })}
        <Link to="/parent/link" className="flex shrink-0 items-center gap-2 rounded-2xl border border-dashed border-border px-3 py-2.5 text-sm font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground">
          <Link2 className="h-4 w-4" /> Add child
        </Link>
      </div>

      {/* Hero */}
      <ChildHero child={active} snapshot={snap} />

      {/* Weekly MAKAI summary */}
      <div className="mt-4">
        <WeeklySummaryCard kind="child" learnerId={active.learnerId} name={active.firstName} />
      </div>


      {/* Stat tiles */}
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          icon={CalendarCheck}
          tone="sky"
          label="Attendance"
          value={snap ? `${Math.round(snap.attendancePct)}%` : "—"}
          hint={snap ? `${snap.absentCount} absent · last 30d` : ""}
        />
        <StatTile
          icon={Wallet}
          tone={snap && snap.feeBalance > 0 ? "warn" : "ok"}
          label="Fee balance"
          value={snap ? fmtMoney(snap.feeBalance, active.currency) : "—"}
          hint={snap && snap.feeOverdue > 0 ? `${fmtMoney(snap.feeOverdue, active.currency)} overdue` : "Up to date"}
          to="/parent/fees"
        />
        <StatTile
          icon={Utensils}
          tone={snap?.cafeteriaLow ? "warn" : "default"}
          label="Cafeteria"
          value={snap?.cafeteriaBalance == null ? "Not set up" : fmtMoney(snap.cafeteriaBalance, active.currency)}
          hint={snap?.cafeteriaLow ? "Low balance" : "Top up anytime"}
          to="/parent/cafeteria"
        />
        <StatTile
          icon={ClipboardList}
          tone={snap && snap.overdueHomework > 0 ? "warn" : "default"}
          label="Homework"
          value={snap ? `${snap.pendingHomework}` : "—"}
          hint={snap && snap.overdueHomework > 0 ? `${snap.overdueHomework} overdue` : "All caught up"}
          to="/parent/homework"
        />
      </div>

      {/* AI summary */}
      <div className="mt-5">
        <AISummaryCard child={active} snapshot={snap} />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        {/* Today's timetable */}
        <Panel title="Today's timetable" icon={CalendarCheck} link={{ to: "/parent/timetable", label: "Full week" }}>
          {!snap ? <Skeleton rows={3} /> : snap.todayTimetable.length === 0 ? (
            <Empty>No classes scheduled today.</Empty>
          ) : (
            <ul className="space-y-1.5">
              {snap.todayTimetable.map((t) => (
                <li key={t.id} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-xl bg-muted/50 px-3 py-2">
                  <span className="text-xs font-semibold text-primary">{t.start.slice(0,5)}</span>
                  <span className="truncate text-sm font-medium">{t.subject}</span>
                  {t.room ? <span className="text-[11px] text-muted-foreground">{t.room}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Homework due */}
        <Panel title="Homework due" icon={ClipboardList} link={{ to: "/parent/homework", label: "All" }}>
          {!snap ? <Skeleton rows={3} /> : snap.homeworkDue.length === 0 ? (
            <Empty>Nothing due in the next 7 days.</Empty>
          ) : (
            <ul className="space-y-1.5">
              {snap.homeworkDue.slice(0, 5).map((h) => (
                <li key={h.id} className="rounded-xl bg-muted/50 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium">{h.title}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{h.due ? formatDue(h.due) : ""}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{h.subject}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Upcoming exams */}
        <Panel title="Upcoming exams" icon={ShieldCheck}>
          {!snap ? <Skeleton rows={3} /> : snap.upcomingExams.length === 0 ? (
            <Empty>No exams scheduled.</Empty>
          ) : (
            <ul className="space-y-1.5">
              {snap.upcomingExams.slice(0, 5).map((e) => (
                <li key={e.id} className="rounded-xl bg-muted/50 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium">{e.title}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{formatDate(e.date)}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{e.subject}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Behaviour */}
        <Panel title="Behaviour notes" icon={AlertTriangle}>
          {!snap ? <Skeleton rows={2} /> : snap.incidents.length === 0 ? (
            <Empty tone="ok">Clean record — keep it up!</Empty>
          ) : (
            <ul className="space-y-2">
              {snap.incidents.slice(0, 4).map((i) => (
                <li key={i.id} className="rounded-xl border border-border/70 bg-card p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold capitalize">{i.category}</span>
                    <span className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase",
                      i.severity === "severe" ? "bg-destructive/15 text-destructive"
                        : i.severity === "moderate" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        : "bg-muted text-muted-foreground",
                    )}>{i.severity}</span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{i.description}</p>
                  <div className="mt-1 text-[10px] text-muted-foreground">{formatDate(i.date)}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Medical alerts */}
        <Panel title="Medical alerts" icon={HeartPulse} link={{ to: "/parent/medical", label: "History" }}>
          {!snap ? <Skeleton rows={2} /> : snap.medicalAlerts.length === 0 ? (
            <Empty tone="ok">No alerts.</Empty>
          ) : (
            <ul className="space-y-2">
              {snap.medicalAlerts.slice(0, 4).map((m) => (
                <li key={m.id} className="rounded-xl border border-border/70 bg-card p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{m.title}</span>
                    <span className="text-[10px] uppercase text-muted-foreground">{m.kind}</span>
                  </div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">{formatDate(m.date)}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Transport */}
        <Panel title="Transport" icon={Bus} link={{ to: "/parent/transport", label: "Map" }}>
          {!snap ? <Skeleton rows={3} /> : !snap.transport ? (
            <Empty>Not enrolled in school transport.</Empty>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between rounded-xl bg-muted/50 px-3 py-2">
                <span className="font-medium">{snap.transport.routeName}</span>
                <span className="text-xs text-muted-foreground">{snap.transport.vehicleReg ?? ""}</span>
              </div>
              {snap.transport.pickupPoint && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" /> Pickup at {snap.transport.pickupPoint}
                </div>
              )}
              <div className={cn(
                "flex items-center justify-between rounded-xl px-3 py-2 text-xs",
                snap.transport.lastPing?.status === "enroute" ? "bg-sky/15 text-sky"
                  : snap.transport.lastPing?.status === "arrived" ? "bg-primary-soft text-primary"
                  : "bg-muted text-muted-foreground",
              )}>
                <span className="font-semibold capitalize">{snap.transport.lastPing?.status ?? "Offline"}</span>
                <span>{snap.transport.lastPing ? timeAgo(snap.transport.lastPing.recorded_at) : "—"}</span>
              </div>
              {snap.transport.driverPhone && (
                <a href={`tel:${snap.transport.driverPhone}`} className="block text-center text-xs font-semibold text-primary underline-offset-2 hover:underline">
                  Call {snap.transport.driverName ?? "driver"} →
                </a>
              )}
            </div>
          )}
        </Panel>
      </div>

      {/* Lower row */}
      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <Panel title="School announcements" icon={Megaphone} link={{ to: "/parent/announcements", label: "All" }} wide>
          {!snap ? <Skeleton rows={2} /> : snap.announcements.length === 0 ? (
            <Empty>No announcements yet.</Empty>
          ) : (
            <ul className="space-y-2">
              {snap.announcements.slice(0, 4).map((a) => (
                <li key={a.id} className="rounded-xl border border-border/70 bg-card p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{a.title}</span>
                    {a.pinned && <span className="rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold text-gold-foreground">Pinned</span>}
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{a.body}</p>
                  <div className="mt-1 text-[10px] text-muted-foreground">{timeAgo(a.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Teacher messages" icon={MessageSquare} link={{ to: "/parent/messages", label: "Inbox" }}>
          {!snap ? <Skeleton rows={2} /> : snap.messages.length === 0 ? (
            <Empty>No messages yet.</Empty>
          ) : (
            <ul className="space-y-2">
              {snap.messages.slice(0, 4).map((m) => (
                <li key={m.id} className="rounded-xl border border-border/70 bg-card p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{m.subject ?? "Message"}</span>
                    {!m.read_at && m.sender === "teacher" && <span className="h-2 w-2 rounded-full bg-primary" />}
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{m.body}</p>
                  <div className="mt-1 text-[10px] text-muted-foreground">{timeAgo(m.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Upcoming events" icon={CalendarDays} link={{ to: "/parent/events", label: "Calendar" }}>
          {!snap ? <Skeleton rows={2} /> : snap.events.length === 0 ? (
            <Empty>Nothing on the school calendar.</Empty>
          ) : (
            <ul className="space-y-2">
              {snap.events.slice(0, 4).map((e) => (
                <li key={e.id} className="rounded-xl border border-border/70 bg-card p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{e.title}</span>
                    <span className="text-[10px] uppercase text-muted-foreground">{e.type}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" /> {formatDateTime(e.start_at)}
                    {e.location && <span>· {e.location}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-6">
        <AICallout
          description={`Ask about ${active.firstName}'s school week — I'll summarise progress, fees and any alerts.`}
          suggestions={[
            `Summarise ${active.firstName} this week`,
            "Any alerts I should act on?",
            "Draft a message to the class teacher",
            "How can I help with this week's homework?",
          ]}
        />
      </div>

      {snapLoading && (
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Refreshing data
        </div>
      )}
    </AppShell>
  );
}

/* ---------------- Hero ---------------- */

function ChildHero({ child, snapshot }: { child: ParentChild; snapshot?: ChildSnapshot }) {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-border bg-gradient-sky p-6 text-white shadow-card">
      <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-gradient-gold opacity-30 blur-3xl" />
      <div className="relative grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
        <Avatar child={child} size={72} dark />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-white/70">{child.schoolName ?? "MAKAI"}</div>
          <h2 className="truncate font-display text-2xl font-bold leading-tight">{child.fullName}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-white/80">
            {child.gradeLabel && <span className="rounded-full bg-white/15 px-2 py-0.5">{child.gradeLabel}</span>}
            {child.admissionNumber && <span className="rounded-full bg-white/15 px-2 py-0.5">#{child.admissionNumber}</span>}
          </div>
        </div>
        <div className="flex gap-2 sm:flex-col">
          <RecentBadge label="Last score" value={snapshot?.recentScores[0] ? `${Math.round(snapshot.recentScores[0].pct)}%` : "—"} />
          <RecentBadge label="Attendance" value={snapshot ? `${Math.round(snapshot.attendancePct)}%` : "—"} />
        </div>
      </div>
    </section>
  );
}

function Avatar({ child, size, dark = false }: { child: ParentChild; size: number; dark?: boolean }) {
  if (child.photoUrl) {
    return (
      <img
        src={child.photoUrl}
        alt={child.fullName}
        style={{ width: size, height: size }}
        className="shrink-0 rounded-2xl object-cover ring-2 ring-white/60"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.35 }}
      className={cn(
        "grid shrink-0 place-items-center rounded-2xl font-display font-bold",
        dark ? "bg-white/15 text-white ring-2 ring-white/30" : "bg-gradient-gold text-gold-foreground",
      )}
    >
      {child.initials}
    </div>
  );
}

function RecentBadge({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-white/10 px-3 py-2 backdrop-blur">
      <div className="text-[10px] uppercase tracking-wider text-white/70">{label}</div>
      <div className="font-display text-lg font-bold leading-none">{value}</div>
    </div>
  );
}

/* ---------------- Stat tile ---------------- */

function StatTile({
  icon: Icon, label, value, hint, tone = "default", to,
}: {
  icon: typeof CalendarCheck;
  label: string; value: string; hint?: string;
  tone?: "default" | "sky" | "ok" | "warn";
  to?: string;
}) {
  const toneClass =
    tone === "sky" ? "bg-sky/15 text-sky"
    : tone === "ok" ? "bg-primary-soft text-primary"
    : tone === "warn" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
    : "bg-muted text-foreground";
  const inner = (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft transition-all hover:shadow-card">
      <div className={cn("grid h-10 w-10 place-items-center rounded-xl", toneClass)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
        <div className="truncate font-display text-lg font-bold leading-tight">{value}</div>
        {hint && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

/* ---------------- AI summary ---------------- */

function AISummaryCard({ child, snapshot }: { child: ParentChild; snapshot?: ChildSnapshot }) {
  const gen = useServerFn(generateChildSummary);
  const [text, setText] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!snapshot) return;
    setLoading(true); setText("");
    try {
      const out = await gen({
        data: {
          learnerName: child.fullName,
          gradeLabel: child.gradeLabel,
          attendancePct: snapshot.attendancePct,
          attendanceWindow: "last 30 days",
          recentScores: snapshot.recentScores,
          pendingHomework: snapshot.pendingHomework,
          overdueHomework: snapshot.overdueHomework,
          recentIncidents: snapshot.incidents.map((i) => ({ category: i.category, severity: i.severity, date: i.date })),
          feeBalance: snapshot.feeBalance,
          currency: child.currency,
        },
      });
      setText(out.text);
    } catch (e) {
      setText(`Could not generate summary: ${(e as Error).message}`);
    } finally { setLoading(false); }
  };

  return (
    <section className="rounded-3xl border border-border bg-card p-5 shadow-soft">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-gold text-gold-foreground"><Sparkles className="h-4 w-4" /></span>
          <div>
            <h3 className="font-display text-sm font-bold">MAKAI's summary</h3>
            <p className="text-[11px] text-muted-foreground">A friendly weekly read on {child.firstName}.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={loading || !snapshot}
          className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground shadow-soft disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {text ? "Regenerate" : "Generate"}
        </button>
      </header>
      <div className="mt-3">
        {!text && !loading && (
          <p className="text-sm text-muted-foreground">Tap Generate for a short, friendly weekly summary covering progress, attendance and what to focus on next.</p>
        )}
        {loading && <Skeleton rows={3} />}
        {text && (
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed dark:prose-invert">{text}</div>
        )}
      </div>
    </section>
  );
}

/* ---------------- Panel + helpers ---------------- */

function Panel({
  title, icon: Icon, link, children, wide = false,
}: {
  title: string;
  icon: typeof CalendarCheck;
  link?: { to: string; label: string };
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <section className={cn("rounded-3xl border border-border bg-card p-5 shadow-soft", wide && "lg:col-span-1")}>
      <header className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-muted text-foreground"><Icon className="h-4 w-4" /></span>
          <h3 className="font-display text-sm font-bold">{title}</h3>
        </div>
        {link && (
          <Link to={link.to} className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
            {link.label} <ChevronRight className="h-3 w-3" />
          </Link>
        )}
      </header>
      {children}
    </section>
  );
}

function Empty({ children, tone = "default" }: { children: React.ReactNode; tone?: "default" | "ok" }) {
  return (
    <div className={cn(
      "rounded-xl border border-dashed px-4 py-6 text-center text-xs",
      tone === "ok" ? "border-primary/30 bg-primary-soft/40 text-primary" : "border-border bg-muted/30 text-muted-foreground",
    )}>{children}</div>
  );
}

function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded-xl bg-muted/60" />
      ))}
    </div>
  );
}

function formatDue(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return `${-diff}d overdue`;
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff <= 7) return `in ${diff}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/* ---------------- Data ---------------- */

async function fetchChildSnapshot(child: ParentChild): Promise<ChildSnapshot> {
  const empty: ChildSnapshot = {
    attendancePct: 100, attendanceDays: 0, absentCount: 0,
    pendingHomework: 0, overdueHomework: 0,
    feeBalance: 0, feeOverdue: 0,
    cafeteriaBalance: null, cafeteriaLow: false,
    recentScores: [], incidents: [], medicalAlerts: [],
    todayTimetable: [], homeworkDue: [], upcomingExams: [],
    announcements: [], messages: [], events: [], transport: null,
  };
  const learnerId = child.learnerId;
  const schoolId = child.schoolId;
  const streamId = child.streamId;
  const since30 = new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10);
  const nowIso = new Date().toISOString();
  const in7 = new Date(Date.now() + 7 * 86400e3).toISOString();

  // Attendance
  const { data: att } = await supabase
    .from("attendance")
    .select("status")
    .eq("learner_id", learnerId)
    .gte("date", since30);
  if (att && att.length > 0) {
    const present = att.filter((a) => a.status === "present" || a.status === "late").length;
    empty.attendancePct = (present / att.length) * 100;
    empty.attendanceDays = att.length;
    empty.absentCount = att.filter((a) => a.status === "absent").length;
  }

  // Invoices (fees)
  const { data: invs } = await supabase
    .from("invoices")
    .select("total_amount, amount_paid, status, due_date")
    .eq("learner_id", learnerId);
  if (invs) {
    for (const inv of invs) {
      const bal = Number(inv.total_amount ?? 0) - Number(inv.amount_paid ?? 0);
      if (bal > 0) {
        empty.feeBalance += bal;
        if (inv.status === "overdue" || (inv.due_date && new Date(inv.due_date) < new Date())) empty.feeOverdue += bal;
      }
    }
  }

  // Cafeteria
  const { data: cafe } = await supabase
    .from("cafeteria_accounts")
    .select("balance, low_threshold")
    .eq("learner_id", learnerId)
    .maybeSingle();
  if (cafe) {
    empty.cafeteriaBalance = Number(cafe.balance ?? 0);
    empty.cafeteriaLow = empty.cafeteriaBalance <= Number(cafe.low_threshold ?? 0);
  }

  // Homework / assignments
  if (streamId) {
    const { data: assignments } = await supabase
      .from("assignments")
      .select("id, title, due_at, subject_id")
      .eq("stream_id", streamId)
      .eq("status", "published")
      .order("due_at", { ascending: true })
      .limit(20);
    const assignmentIds = (assignments ?? []).map((a) => a.id);
    let submissionsByAssignment: Record<string, { status: string }> = {};
    if (assignmentIds.length > 0) {
      const { data: subs } = await supabase
        .from("assignment_submissions")
        .select("assignment_id, status")
        .eq("learner_id", learnerId)
        .in("assignment_id", assignmentIds);
      for (const s of subs ?? []) submissionsByAssignment[s.assignment_id] = { status: s.status };
    }
    const subjectIds = Array.from(new Set((assignments ?? []).map((a) => a.subject_id).filter(Boolean)));
    let subjectMap: Record<string, string> = {};
    if (subjectIds.length > 0) {
      const { data: subj } = await supabase.from("subjects_catalog").select("id, name").in("id", subjectIds);
      for (const s of subj ?? []) subjectMap[s.id] = s.name;
    }
    for (const a of assignments ?? []) {
      const sub = submissionsByAssignment[a.id];
      const done = sub && (sub.status === "submitted" || sub.status === "graded");
      const overdue = !done && a.due_at && new Date(a.due_at) < new Date();
      if (!done) empty.pendingHomework += 1;
      if (overdue) empty.overdueHomework += 1;
      if (!done && a.due_at && new Date(a.due_at) <= new Date(in7)) {
        empty.homeworkDue.push({
          id: a.id, title: a.title,
          subject: subjectMap[a.subject_id] ?? "Subject",
          due: a.due_at,
        });
      }
    }
  }

  // Recent scores (assessment_marks)
  const { data: marks } = await supabase
    .from("assessment_marks")
    .select("score, created_at, assessment_id, assessments:assessment_id(title, max_score, subject_id, assessed_on)")
    .eq("learner_id", learnerId)
    .order("created_at", { ascending: false })
    .limit(6);
  if (marks) {
    const subjectIds = Array.from(new Set(
      marks.map((m) => (m as { assessments?: { subject_id?: string } }).assessments?.subject_id).filter(Boolean) as string[],
    ));
    let subjectMap: Record<string, string> = {};
    if (subjectIds.length > 0) {
      const { data: subj } = await supabase.from("subjects_catalog").select("id, name").in("id", subjectIds);
      for (const s of subj ?? []) subjectMap[s.id] = s.name;
    }
    for (const m of marks) {
      const a = (m as { assessments?: { title?: string; max_score?: number; subject_id?: string; assessed_on?: string } }).assessments;
      if (!a || !m.score) continue;
      const max = Number(a.max_score ?? 100);
      if (!max) continue;
      empty.recentScores.push({
        subject: subjectMap[a.subject_id ?? ""] ?? "Subject",
        title: a.title ?? "Assessment",
        pct: (Number(m.score) / max) * 100,
        date: a.assessed_on ?? new Date(m.created_at as string).toISOString().slice(0, 10),
      });
    }
  }

  // Discipline incidents
  const { data: inc } = await supabase
    .from("discipline_incidents")
    .select("id, category, severity, incident_date, description")
    .eq("learner_id", learnerId)
    .gte("incident_date", since30)
    .order("incident_date", { ascending: false })
    .limit(6);
  empty.incidents = (inc ?? []).map((i) => ({
    id: i.id, category: i.category, severity: i.severity, date: i.incident_date, description: i.description,
  }));

  // Medical alerts
  const { data: med } = await supabase
    .from("medical_records")
    .select("id, kind, title, occurred_at")
    .eq("learner_id", learnerId)
    .order("occurred_at", { ascending: false })
    .limit(5);
  empty.medicalAlerts = (med ?? []).map((m) => ({
    id: m.id, kind: m.kind, title: m.title, date: m.occurred_at,
  }));

  // Today's timetable
  if (streamId) {
    const { data: slots } = await supabase
      .from("timetable_slots")
      .select("id, start_time, end_time, room, subject_id, day")
      .eq("stream_id", streamId)
      .eq("day", todayKey())
      .order("start_time");
    if (slots && slots.length > 0) {
      const subjectIds = Array.from(new Set(slots.map((s) => s.subject_id).filter(Boolean) as string[]));
      let subjectMap: Record<string, string> = {};
      if (subjectIds.length > 0) {
        const { data: subj } = await supabase.from("subjects_catalog").select("id, name").in("id", subjectIds);
        for (const s of subj ?? []) subjectMap[s.id] = s.name;
      }
      empty.todayTimetable = slots.map((s) => ({
        id: s.id, start: s.start_time, end: s.end_time, room: s.room ?? null,
        subject: s.subject_id ? subjectMap[s.subject_id] ?? "Class" : "Class",
      }));
    }
  }

  // Upcoming exams (assessments table)
  if (streamId) {
    const today = new Date().toISOString().slice(0, 10);
    const { data: ex } = await supabase
      .from("assessments")
      .select("id, title, subject_id, assessed_on")
      .eq("stream_id", streamId)
      .gte("assessed_on", today)
      .order("assessed_on", { ascending: true })
      .limit(5);
    if (ex && ex.length > 0) {
      const subjectIds = Array.from(new Set(ex.map((e) => e.subject_id).filter(Boolean) as string[]));
      const subjectMap: Record<string, string> = {};
      if (subjectIds.length > 0) {
        const { data: subj } = await supabase.from("subjects_catalog").select("id, name").in("id", subjectIds);
        for (const s of subj ?? []) subjectMap[s.id] = s.name;
      }
      empty.upcomingExams = ex
        .filter((e) => !!e.assessed_on)
        .map((e) => ({
          id: e.id, title: e.title,
          subject: e.subject_id ? subjectMap[e.subject_id] ?? "Subject" : "Subject",
          date: e.assessed_on as string,
        }));
    }
  }

  // Announcements (parents + school audience for the school, or class for the stream)
  if (schoolId) {
    const { data: ann } = await supabase
      .from("announcements")
      .select("id, title, body, created_at, pinned, audience, stream_id")
      .eq("school_id", schoolId)
      .order("pinned", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(10);
    empty.announcements = (ann ?? [])
      .filter((a) => a.audience !== "class" || (streamId && a.stream_id === streamId))
      .map((a) => ({ id: a.id, title: a.title, body: a.body, created_at: a.created_at, pinned: a.pinned }));
  }

  // Teacher messages (this parent ↔ teachers, for this learner if linked)
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    const { data: msgs } = await supabase
      .from("teacher_messages")
      .select("id, subject, body, created_at, read_at, sender, learner_id")
      .eq("parent_user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(10);
    empty.messages = (msgs ?? [])
      .filter((m) => !m.learner_id || m.learner_id === learnerId)
      .map((m) => ({
        id: m.id, subject: m.subject, body: m.body,
        created_at: m.created_at, read_at: m.read_at, sender: m.sender,
      }));
  }

  // School events
  if (schoolId) {
    const { data: ev } = await supabase
      .from("school_events")
      .select("id, title, start_at, location, type")
      .eq("school_id", schoolId)
      .gte("start_at", nowIso)
      .order("start_at", { ascending: true })
      .limit(6);
    empty.events = (ev ?? []).map((e) => ({
      id: e.id, title: e.title, start_at: e.start_at, location: e.location, type: e.type,
    }));
  }

  // Transport
  const { data: ta } = await supabase
    .from("transport_assignments")
    .select("pickup_point, route_id, routes:route_id(name, vehicle_reg, driver_name, driver_phone)")
    .eq("learner_id", learnerId)
    .maybeSingle();
  if (ta?.route_id) {
    const r = (ta as { routes?: { name?: string; vehicle_reg?: string | null; driver_name?: string | null; driver_phone?: string | null } | null }).routes;
    const { data: ping } = await supabase
      .from("transport_pings")
      .select("status, recorded_at, lat, lng")
      .eq("route_id", ta.route_id)
      .order("recorded_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    empty.transport = {
      routeName: r?.name ?? "Route",
      vehicleReg: r?.vehicle_reg ?? null,
      driverName: r?.driver_name ?? null,
      driverPhone: r?.driver_phone ?? null,
      pickupPoint: ta.pickup_point ?? null,
      lastPing: ping ?? null,
    };
  }

  return empty;
}
